// Client-side iCalendar (RFC 5545) export for events: one VCALENDAR with one
// VEVENT, downloaded through the same `download()` helper as track exports.
import { download, slugify } from "./export";
import type { EventRecurrence } from "./format";

export type IcsEventInput = {
  id: string;
  title: string;
  description?: string | null;
  /** timestamptz ISO string */
  startsAt: string;
  endsAt?: string | null;
  /** Place name, or a "lat, lng" string when the event only has a map point. */
  location?: string | null;
  recurrence: EventRecurrence;
  /** date string (YYYY-MM-DD) */
  recurrenceUntil?: string | null;
};

/** RFC 5545 TEXT escaping: backslash first, then semicolons, commas, newlines. */
function escapeText(s: string): string {
  return s.replaceAll("\\", "\\\\").replaceAll(";", "\\;").replaceAll(",", "\\,").replace(/\r?\n/g, "\\n");
}

/** ISO timestamp → UTC basic format ("2026-08-11T11:22:19.301Z" → "20260811T112219Z"). */
function toUtcBasic(iso: string): string {
  return new Date(iso).toISOString().replace(/\.\d{3}/, "").replaceAll("-", "").replaceAll(":", "");
}

const RRULE_FREQ: Partial<Record<EventRecurrence, string>> = {
  DAILY: "FREQ=DAILY",
  WEEKLY: "FREQ=WEEKLY",
  // iCalendar has no biweekly frequency — it's WEEKLY at interval 2.
  BIWEEKLY: "FREQ=WEEKLY;INTERVAL=2",
  MONTHLY: "FREQ=MONTHLY",
};

export function eventToIcs(ev: IcsEventInput): string {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//gaea//events//EN",
    "BEGIN:VEVENT",
    `UID:${ev.id}@gaea`,
    `DTSTAMP:${toUtcBasic(new Date().toISOString())}`,
    `DTSTART:${toUtcBasic(ev.startsAt)}`,
  ];
  if (ev.endsAt) lines.push(`DTEND:${toUtcBasic(ev.endsAt)}`);
  lines.push(`SUMMARY:${escapeText(ev.title)}`);
  if (ev.description) lines.push(`DESCRIPTION:${escapeText(ev.description)}`);
  if (ev.location) lines.push(`LOCATION:${escapeText(ev.location)}`);
  const freq = RRULE_FREQ[ev.recurrence];
  if (freq) {
    // recurrence_until is a bare date; end-of-day UTC keeps the last
    // occurrence inside the rule.
    const until = ev.recurrenceUntil ? `;UNTIL=${ev.recurrenceUntil.replaceAll("-", "")}T235959Z` : "";
    lines.push(`RRULE:${freq}${until}`);
  }
  lines.push("END:VEVENT", "END:VCALENDAR", "");
  // RFC 5545 mandates CRLF line endings.
  return lines.join("\r\n");
}

export function downloadEventIcs(ev: IcsEventInput): void {
  download(`${slugify(ev.title)}.ics`, "text/calendar", eventToIcs(ev));
}
